import clsx from "clsx";
import { useMemo, useState } from "react";

import {
  NO_SCENE_CONTEXT_COLOR,
  NO_SCENE_CONTEXT_ID,
  getSceneContextElementCounts,
  getUnknownSceneContextIds,
} from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import {
  actionDeleteSceneContext,
  actionMoveSelectionToSceneContext,
  getSceneContextDisplayName,
} from "../actions/actionSceneContexts";
import { t } from "../i18n";

import { Dialog } from "./Dialog";
import DialogActionButton from "./DialogActionButton";

import "./SceneContextsMenu.scss";

import type { ActionManager } from "../actions/manager";
import type { UIAppState } from "../types";

type SceneContextOption = { key: string; name: string; color: string };

const getSceneContextOptions = (
  elements: readonly ExcalidrawElement[],
  sceneContexts: UIAppState["sceneContexts"],
): SceneContextOption[] => [
  ...sceneContexts.map((sceneContext) => ({
    key: sceneContext.id,
    name: getSceneContextDisplayName(sceneContext.id, sceneContexts),
    color: sceneContext.color,
  })),
  ...getUnknownSceneContextIds(elements, sceneContexts).map((id) => ({
    key: id,
    name: `${t("sceneContexts.unknownContext")} (${id.slice(0, 6)})`,
    color: NO_SCENE_CONTEXT_COLOR,
  })),
  {
    key: NO_SCENE_CONTEXT_ID,
    name: t("sceneContexts.noContext"),
    color: NO_SCENE_CONTEXT_COLOR,
  },
];

const OptionLabel = ({ option }: { option: SceneContextOption }) => (
  <>
    <span
      className="scene-contexts__swatch"
      style={{ backgroundColor: option.color }}
      aria-hidden="true"
    />
    <span className="scene-contexts__name-text">{option.name}</span>
  </>
);

/** picks the context the selection is moved to */
export const MoveToSceneContextDialog = ({
  elements,
  appState,
  actionManager,
  onClose,
}: {
  elements: readonly ExcalidrawElement[];
  appState: UIAppState;
  actionManager: ActionManager;
  onClose: () => void;
}) => {
  const options = useMemo(
    () => getSceneContextOptions(elements, appState.sceneContexts),
    [elements, appState.sceneContexts],
  );

  return (
    <Dialog
      onCloseRequest={onClose}
      title={t("sceneContexts.moveToTitle")}
      size="small"
      className="scene-context-dialog"
    >
      <ul className="scene-context-dialog__options">
        {options.map((option) => (
          <li key={option.key}>
            <button
              type="button"
              className="scene-context-dialog__option"
              data-testid={`scene-context-move-${option.key}`}
              onClick={() =>
                actionManager.executeAction(
                  actionMoveSelectionToSceneContext,
                  "ui",
                  { id: option.key },
                )
              }
            >
              <OptionLabel option={option} />
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
};

/** asks where the elements of a context that's being deleted go */
export const DeleteSceneContextDialog = ({
  sceneContextId,
  elements,
  appState,
  actionManager,
  onClose,
}: {
  sceneContextId: string;
  elements: readonly ExcalidrawElement[];
  appState: UIAppState;
  actionManager: ActionManager;
  onClose: () => void;
}) => {
  const options = useMemo(
    () =>
      getSceneContextOptions(elements, appState.sceneContexts).filter(
        (option) => option.key !== sceneContextId,
      ),
    [elements, appState.sceneContexts, sceneContextId],
  );
  const [targetKey, setTargetKey] = useState(NO_SCENE_CONTEXT_ID);
  const count = getSceneContextElementCounts(elements).get(sceneContextId) ?? 0;
  const name = getSceneContextDisplayName(
    sceneContextId,
    appState.sceneContexts,
  );

  return (
    <Dialog
      onCloseRequest={onClose}
      title={t("sceneContexts.deleteTitle", { name })}
      size="small"
      className="scene-context-dialog"
    >
      {count > 0 ? (
        <>
          <p>{t("sceneContexts.deleteDescription", { count })}</p>
          <ul className="scene-context-dialog__options" role="radiogroup">
            {options.map((option) => (
              <li key={option.key}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={option.key === targetKey}
                  className={clsx("scene-context-dialog__option", {
                    "scene-context-dialog__option--selected":
                      option.key === targetKey,
                  })}
                  data-testid={`scene-context-delete-target-${option.key}`}
                  onClick={() => setTargetKey(option.key)}
                >
                  <OptionLabel option={option} />
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p>{t("sceneContexts.deleteDescriptionEmpty")}</p>
      )}
      <div className="confirm-dialog-buttons">
        <DialogActionButton label={t("buttons.cancel")} onClick={onClose} />
        <DialogActionButton
          label={t("sceneContexts.delete")}
          actionType="danger"
          data-testid="scene-context-delete-confirm"
          onClick={() =>
            actionManager.executeAction(actionDeleteSceneContext, "ui", {
              id: sceneContextId,
              targetId: targetKey === NO_SCENE_CONTEXT_ID ? null : targetKey,
            })
          }
        />
      </div>
    </Dialog>
  );
};
